import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface TenantDocument {
  id: string;
  name: string;
  originalName: string;
  type: string;
  mimeType: string;
  size: number;
  filePath: string;
  description?: string;
  uploadedBy: string;
  isActive: boolean;
  metadata?: Record<string, any>;
  tenantId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateTenantDocumentRequest {
  name: string;
  originalName: string;
  type: string;
  mimeType: string;
  size: number;
  filePath: string;
  description?: string;
  uploadedBy: string;
  tenantId: string;
  metadata?: Record<string, any>;
}

export interface UpdateTenantDocumentRequest {
  name?: string;
  type?: string;
  description?: string;
  isActive?: boolean;
  metadata?: Record<string, any>;
}

@Injectable({
  providedIn: 'root'
})
export class TenantDocumentService {
  private readonly API_URL = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getTenantDocuments(tenantId: string): Observable<TenantDocument[]> {
    return this.http.get<TenantDocument[]>(`${this.API_URL}/tenants/${tenantId}/documents`);
  }

  getTenantDocument(tenantId: string, documentId: string): Observable<TenantDocument> {
    return this.http.get<TenantDocument>(`${this.API_URL}/tenants/${tenantId}/documents/${documentId}`);
  }

  uploadDocument(tenantId: string, file: File, type?: string, description?: string): Observable<TenantDocument> {
    const formData = new FormData();
    formData.append('file', file);
    if (type) formData.append('type', type);
    if (description) formData.append('description', description);

    return this.http.post<TenantDocument>(`${this.API_URL}/tenants/${tenantId}/documents/upload`, formData);
  }

  createDocument(tenantId: string, request: CreateTenantDocumentRequest): Observable<TenantDocument> {
    return this.http.post<TenantDocument>(`${this.API_URL}/tenants/${tenantId}/documents`, request);
  }

  updateDocument(tenantId: string, documentId: string, request: UpdateTenantDocumentRequest): Observable<TenantDocument> {
    return this.http.put<TenantDocument>(`${this.API_URL}/tenants/${tenantId}/documents/${documentId}`, request);
  }

  deleteDocument(tenantId: string, documentId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.API_URL}/tenants/${tenantId}/documents/${documentId}`);
  }

  downloadDocument(tenantId: string, documentId: string): Observable<Blob> {
    return this.http.get(`${this.API_URL}/tenants/${tenantId}/documents/${documentId}/download`, {
      responseType: 'blob'
    });
  }

  getDocumentViewUrl(tenantId: string, documentId: string): string {
    return `${this.API_URL}/tenants/${tenantId}/documents/${documentId}/view`;
  }

  // Utility methods
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getDocumentIcon(type: string): string {
    const iconMap: { [key: string]: string } = {
      'Contract': 'description',
      'License': 'verified',
      'Insurance': 'security',
      'Technical': 'engineering',
      'Document': 'description',
      'Spreadsheet': 'table_chart',
      'Image': 'image',
      'Archive': 'archive',
      'Other': 'insert_drive_file'
    };
    return iconMap[type] || iconMap['Other'];
  }

  isImageType(mimeType: string): boolean {
    return mimeType.startsWith('image/');
  }

  isPdfType(mimeType: string): boolean {
    return mimeType === 'application/pdf';
  }

  canPreview(mimeType: string): boolean {
    return this.isImageType(mimeType) || this.isPdfType(mimeType);
  }
}
